import { AsyncPipe, CurrencyPipe, NgClass, NgFor, NgIf, NgStyle, SlicePipe, TitleCasePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { CustomCurrencyPipe } from '@shared/pipes/custom-currency.pipe';
import { CustomDatePipe } from '@shared/pipes/custom-date.pipe';
import { CustomisableVariablesService } from '@shared/services/customisable-variables.service';
import { AccessibleClickDirective } from '@shared/shared/directives/accessible-click.directive';
import { CurrencyEnum } from '@shared/shared/enums/currency.enum';
import { Card, CardStyleEnum } from '../../models/card';
import { BadgeComponent } from '../badge/badge.component';
import { CardBookingCtaComponent } from '../card-booking-cta/card-booking-cta.component';
import { CardRatingComponent } from '../card-rating/card-rating.component';

@Component({
  selector: 'ten-card-body',
  templateUrl: './card-body.component.html',
  styleUrls: ['./card-body.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    NgClass,
    NgIf,
    NgFor,
    BadgeComponent,
    CardRatingComponent,
    SlicePipe,
    TitleCasePipe,
    TranslateModule,
    NgStyle,
    CurrencyPipe,
    CustomCurrencyPipe,
    AccessibleClickDirective,
    MatIconModule,
    MatButtonModule,
    AsyncPipe,
    CustomDatePipe,
    CardBookingCtaComponent,
  ],
})
export class CardBodyComponent implements OnInit {
  @Input() public currency: string;
  @Input() public badgesOnTop = false;
  @Input() public starSvgHtml: string;
  @Input() public asyncPageLoad = false;
  @Input() public showCardBottom = false;
  @Output() public openModalParent = new EventEmitter<void>();
  @Output() public cardClickedParent = new EventEmitter<Event>();
  @Output() public isShowMoreInfo = new EventEmitter<boolean>();

  private _cardData: Card;
  public get cardData(): Card {
    return this._cardData;
  }
  @Input() public set cardData(value: Card) {
    this._cardData = value;
  }

  public showMoreInfo = false;
  public displayFilters = false;
  public showAmenities = false;
  public showBenefits = false;
  public isAmenities = false;
  public currencyEnum = CurrencyEnum;
  public bookingDescription = '';
  public expiringSoon: boolean;
  public newOffer: boolean;

  constructor(public customVariablesService: CustomisableVariablesService) {}

  public ngOnInit() {
    const description = this.cardData.body.booking?.description;

    if (description && !description.includes('undefined')) {
      this.bookingDescription = `<p>${description.replace(/<p>/g, '').replace(/<\/p>/g, '<br/>')}</p>`;
    }
    this.isAmenities = this.checkIsAmenities();
    this.displayFilters = this.checkIsDisplayFilters();
    this.showAmenities = this.checkIsShowAmenities();
    this.showBenefits = this.cardData.type === 'prelogin-benefits';
  }

  private checkIsAmenities(): boolean {
    return !!this.cardData.body.contentSections?.some((section) => section.content.some((value) => !!value));
  }

  private checkIsShowAmenities(): boolean {
    return (
      this.cardData.type === 'hotel' &&
      (this.cardData.cardStyle === CardStyleEnum.minimal ||
        this.cardData.cardStyle === CardStyleEnum.minimal_alt ||
        (this.cardData.cardStyle === CardStyleEnum.halfnhalf && !!this.cardData.body.contentSections[0]?.content[0]))
    );
  }

  public seeMore(event: Event) {
    this.showMoreInfo = !this.showMoreInfo;
    this.isShowMoreInfo.emit(this.showMoreInfo);
    event.preventDefault();
    event.stopPropagation();
  }

  public openModal() {
    this.openModalParent.next();
  }

  public onCardClicked(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.cardClickedParent.next(event);
  }

  private checkIsDisplayFilters(): boolean {
    if (!this.cardData) {
      return false;
    }

    const filterActive =
      !!this.cardData.filters?.category?.length ||
      !!this.cardData.filters?.cuisines?.length ||
      this.cardData.filters?.heldTable ||
      this.cardData.filters?.memberBenefit;

    return this.cardData.type === 'restaurant' || (this.cardData.type === 'hotTable' && filterActive);
  }
}
